import type { MetadataFieldType } from '@iconik-io/iconik-api/src/types/metadata_field_type';
import type {
  CanonicalFilterMapping,
  MultiSelectFilterModifier,
} from '@iconik-io/shared-lib/src/redux/common_filters/types';

import { STORAGE_ID_TERM_NAMES } from './storages';

export const STORAGE_CANONICAL_MAPPING: CanonicalFilterMapping = {
  canonicalKey: 'storage_id',
  nestedKeys: STORAGE_ID_TERM_NAMES,
};

export const DEFAULT_CANONICAL_MAPPINGS: CanonicalFilterMapping[] = [
  STORAGE_CANONICAL_MAPPING,
];

export const DEFAULT_MULTI_SELECT_FIELD_NAMES: ReadonlySet<string> = new Set([
  'storage_id',
  'person_ids',
  'created_by_user',
  'deleted_by_user',
  'transcription_text',
]);

// Custom metadata field types that, when used as a search filter, are rendered
// as a multi-select filter with modifiers (include/exclude terms) rather than a
// plain single-value input.
const MULTI_SELECT_TEXT_FIELD_TYPE_LIST = [
  'string',
  'string_exact',
  'text',
  'email',
  'url',
  'integer',
  'float',
] as const satisfies readonly MetadataFieldType[];

// Derived from the same list as the set, so membership proves the narrowing.
export type MultiSelectTextFieldType =
  (typeof MULTI_SELECT_TEXT_FIELD_TYPE_LIST)[number];

export const MULTI_SELECT_TEXT_FIELD_TYPES: ReadonlySet<MetadataFieldType> =
  new Set(MULTI_SELECT_TEXT_FIELD_TYPE_LIST);

export const COLLECTIONS_ONLY_MODIFIERS: MultiSelectFilterModifier[] = [
  'include_any_of',
  'exclude_any_of',
];

// Single-select drop_down fields cannot express an "all of" match (only one
// value is ever set), so they offer include/exclude "any of" only.
export const SINGLE_SELECT_DROP_DOWN_MODIFIERS: MultiSelectFilterModifier[] = [
  'include_any_of',
  'exclude_any_of',
];

/**
 * The modifiers a metadata field offers in its search filter, matching the
 * panel's field renderer: a single-select `drop_down` is restricted to
 * `*_any_of`, every other field allows the full set (`undefined`). Shared by the
 * filter panel and the active-filter chips.
 */
export function getMetadataFieldAvailableModifiers(
  fieldType: MetadataFieldType,
  multi: boolean | null | undefined,
): MultiSelectFilterModifier[] | undefined {
  if (fieldType === 'drop_down' && !multi) {
    return SINGLE_SELECT_DROP_DOWN_MODIFIERS;
  }
  return undefined;
}

/**
 * Storage `*_all_of` modifiers can never match when only collections are
 * selected and any single group targets more than one storage: a collection
 * lives on exactly one storage, so an AND across storages yields nothing. In
 * that case callers clamp the `*_all_of` modifiers to `*_any_of` (see
 * `COLLECTIONS_ONLY_MODIFIERS`). Both the search filters panel and the NLTF
 * normalizer share this predicate so the clamp threshold stays consistent.
 */
export function shouldClampCollectionsModifiers(
  onlyCollectionsSelected: boolean,
  maxStoragesInAnyGroup: number,
): boolean {
  return onlyCollectionsSelected && maxStoragesInAnyGroup > 1;
}
